import { STOCK_LOCATION_TYPE_TO_PACKAGING_METHOD } from "../constants/deviceTypeMapping";

/**
 * Service to handle device access filtering based on user permissions
 */
export default class DeviceAccessService {
  /**
   * Check if a user has access to a specific device
   * @param {Object} user - The user object
   * @param {Object|String} device - The device object or device ID
   * @returns {boolean} - True if user has access, false otherwise
   */
  static userHasAccessToDevice(user, device) {
    if (!user || !device) return false;

    // New: type-based access via preferredDeviceTypes
    if (user.preferredDeviceTypes && user.preferredDeviceTypes.length > 0) {
      const deviceType = typeof device === 'object'
        ? (device.type?.type || null)
        : null;
      const parentType = typeof device === 'object'
        ? (device.parent?.type?.type || null)
        : null;

      const packagingMethod = deviceType
        ? STOCK_LOCATION_TYPE_TO_PACKAGING_METHOD[deviceType]
        : null;
      const parentPackagingMethod = parentType
        ? STOCK_LOCATION_TYPE_TO_PACKAGING_METHOD[parentType]
        : null;

      return (
        (packagingMethod && user.preferredDeviceTypes.includes(packagingMethod)) ||
        (parentPackagingMethod && user.preferredDeviceTypes.includes(parentPackagingMethod))
      );
    }

    // Legacy: ID-based access via devices array
    if (user.devices && user.devices.length > 0) {
      const deviceId = typeof device === 'object' ?
        (device._id || device.id) : device;

      // Also check the parent device ID (users are assigned to parent devices,
      // but stocked items may reference child devices)
      const parentId = typeof device === 'object' ?
        (device.parent?._id || device.parent) : null;

      return user.devices.some(userDevice => {
        const userDeviceId = typeof userDevice === 'object' ?
          (userDevice._id || userDevice.id) : userDevice;
        return userDeviceId === deviceId || (parentId && userDeviceId === parentId);
      });
    }

    // Enterprise users with no devices assigned have access to all devices
    if (user.isEnterprise) return true;

    // If user has no devices assigned, they have access to all devices in their sites
    if (user.sites && user.sites.length > 0) {
      const deviceSiteId = typeof device === 'object' ?
        (device.site?._id || device.site) : null;

      if (deviceSiteId) {
        return user.sites.some(site =>
          (site._id || site) === deviceSiteId
        );
      }
    }
    return true; // If no site restrictions, allow access
  }

  /**
   * Filter a list of devices based on user access
   * @param {Object} user - The user object
   * @param {Array} devices - Array of devices to filter
   * @returns {Array} - Filtered array of devices user has access to
   */
  static filterDevicesByUserAccess(user, devices) {
    if (!user || !devices || !Array.isArray(devices)) return [];

    // New: type-based filtering via preferredDeviceTypes
    if (user.preferredDeviceTypes && user.preferredDeviceTypes.length > 0) {
      return devices.filter(device => {
        const deviceType = device.type?.type || null;
        const parentType = device.parent?.type?.type || null;

        const packagingMethod = deviceType
          ? STOCK_LOCATION_TYPE_TO_PACKAGING_METHOD[deviceType]
          : null;
        const parentPackagingMethod = parentType
          ? STOCK_LOCATION_TYPE_TO_PACKAGING_METHOD[parentType]
          : null;

        return (
          (packagingMethod && user.preferredDeviceTypes.includes(packagingMethod)) ||
          (parentPackagingMethod && user.preferredDeviceTypes.includes(parentPackagingMethod))
        );
      });
    }

    // Legacy: ID-based filtering via devices array
    if (user.devices && user.devices.length > 0) {
      const userDeviceIds = user.devices.map(d =>
        typeof d === 'object' ? (d._id || d.id) : d
      );

      return devices.filter(device => {
        const deviceId = device._id || device.id;
        const parentId = device.parent?._id || device.parent;
        return userDeviceIds.includes(deviceId) || (parentId && userDeviceIds.includes(parentId));
      });
    }

    // Enterprise users with no devices assigned see all devices
    if (user.isEnterprise) return devices;

    // If user has sites but no specific devices, filter by sites
    if (user.sites && user.sites.length > 0) {
      const userSiteIds = user.sites.map(s =>
        typeof s === 'object' ? (s._id || s.id) : s
      );

      return devices.filter(device => {
        const deviceSiteId = typeof device.site === 'object' ?
          (device.site._id || device.site.id) : device.site;
        return userSiteIds.includes(deviceSiteId);
      });
    }

    // Default: return all devices if no restrictions
    return devices;
  }

  /**
   * Get accessible device IDs for a user
   * @param {Object} user - The user object
   * @returns {Array|null} - Array of device IDs user has access to, or null if no ID-based filtering
   */
  static getAccessibleDeviceIds(user) {
    if (!user) return [];

    // Type-based filtering doesn't use IDs
    if (user.preferredDeviceTypes && user.preferredDeviceTypes.length > 0) {
      return null;
    }

    // If user has specific devices, return those IDs
    if (user.devices && user.devices.length > 0) {
      return user.devices.map(d =>
        typeof d === 'object' ? (d._id || d.id) : d
      );
    }

    // Enterprise users or users without device restrictions
    // would need to fetch all devices - return null to indicate no filtering
    return null;
  }
}
